import React, { useRef, useEffect, useState } from 'react';
import Draggable, { DraggableData, DraggableEvent, ControlPosition } from 'react-draggable';
import Paper from '@mui/material/Paper';

interface DraggablePaperProps extends React.ComponentProps<typeof Paper> {
  children?: React.ReactNode;
}

const DraggablePaper: React.FC<DraggablePaperProps> = ({ id, ...paperProps }) => {
  const nodeRef = useRef(null);
  const [position, setPosition] = useState<ControlPosition>(() => {
    if (id) {
      try {
        const saved = localStorage.getItem(`glynt:dialog-pos:${id}`);
        if (saved) {
          const parsed = JSON.parse(saved) as { x: number; y: number };
          if (
            typeof parsed?.x === 'number' &&
            typeof parsed?.y === 'number' &&
            isFinite(parsed.x) &&
            isFinite(parsed.y)
          ) {
            return { x: parsed.x, y: parsed.y } as ControlPosition;
          }
        }
      } catch (_) {
        // ignore malformed storage
      }
    }
    return { x: 0, y: 0 } as ControlPosition;
  });
  const [contentHeight, setContentHeight] = useState(0);

  // Monitor content height changes and reposition if needed
  useEffect(() => {
    if (nodeRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const newHeight = entry.contentRect.height;
          if (newHeight !== contentHeight) {
            setContentHeight(newHeight);

            // Check if dialog is cut off at the top
            const rect = entry.target.getBoundingClientRect();
            const viewportHeight = window.innerHeight;

            if (rect.top < 0) {
              // Dialog is cut off at the top, reposition to center
              const newY = Math.max(0, (viewportHeight - newHeight) / 2);
              setPosition({ x: position.x, y: newY });
            }
          }
        }
      });

      resizeObserver.observe(nodeRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [contentHeight, position.x]);

  const handleStop = (e: DraggableEvent, data: DraggableData) => {
    setPosition({ x: data.x, y: data.y });

    // Persist position if an id is available
    if (id) {
      const storageKey = `glynt:dialog-pos:${id}`;
      try {
        localStorage.setItem(storageKey, JSON.stringify({ x: data.x, y: data.y }));
      } catch (_) {
        // ignore storage write errors
      }
    }
  };

  const handleDrag = (_e: DraggableEvent, data: DraggableData) => {
    setPosition({ x: data.x, y: data.y });
  };

  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
      position={position}
      onDrag={handleDrag}
      onStop={handleStop}
    >
      <Paper ref={nodeRef} id={id} {...paperProps} />
    </Draggable>
  );
};

export default DraggablePaper;
